import React, { useEffect, useRef } from "react";
import { CloseOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, theme, Tooltip } from "antd";

import { type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";

import { traineeKeys } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase } from "~/store/initializeStore";
import { getTraineeDisplayName } from "./OpenTraineeModal";

interface TraineeTabName {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  user: { firstName: string | null; lastName: string | null; avatarUrl: string | null } | null;
}

// Nazwy wszystkich kart jednym zapytaniem. Pełny profil na kartę (`select=*` z joinami) dawał
// przy 10 zapamiętanych kartach 10 zapytań na każde wejście do profilu (TRE-2327).
const useTraineeTabNames = (traineeIds: string[]) =>
  useQuery({
    queryKey: ["traineeTabNames", [...traineeIds].sort()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainee")
        .select("id, firstName, lastName, email, user:userId(firstName, lastName, avatarUrl)")
        .in("id", traineeIds)
        .returns<TraineeTabName[]>();

      if (error) {
        throw new Error(error.message);
      }

      return new Map(data.map((trainee) => [trainee.id, trainee]));
    },
    enabled: traineeIds.length > 0,
    // Otwarcie kolejnej karty zmienia klucz - pozostałe karty nie wracają wtedy do „Wczytywanie...".
    keepPreviousData: true,
  });

interface TraineeTabStripProps {
  traineeIds: string[];
  activeId: string;
  onSelect: (traineeId: string) => void;
  onClose: (traineeId: string) => void;
  onAdd: () => void;
}

/**
 * Pasek kart klientek w stylu przeglądarki: aktywna karta ma tło treści profilu i łączy się
 * z nią, pozostałe są płaskie. Środkowy przycisk myszy zamyka kartę.
 */
export const TraineeTabStrip = ({ traineeIds, activeId, onSelect, onClose, onAdd }: TraineeTabStripProps) => {
  const { token } = theme.useToken();
  const listRef = useRef<HTMLDivElement>(null);
  const { data: tabNames } = useTraineeTabNames(traineeIds);

  // Aktywna karta ma być widoczna po przełączeniu, otwarciu nowej i po zwężeniu okna.
  // Przewijamy samą listę, nie stronę (scrollIntoView potrafi ruszyć też przewijanie pionowe).
  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    const revealActive = () => {
      const tab = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!tab) {
        return;
      }
      const left = tab.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
      const right = left + tab.offsetWidth;
      if (left < list.scrollLeft) {
        list.scrollLeft = left;
      } else if (right > list.scrollLeft + list.clientWidth) {
        list.scrollLeft = right - list.clientWidth;
      }
    };

    revealActive();
    const observer = new ResizeObserver(revealActive);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeId, traineeIds.length]);

  return (
    <div
      // Pasek wychodzi poza padding obszaru treści (`p-4 lg:p-8` w Dashboardzie) i przykleja się
      // pod nagłówkiem aplikacji (`h-16`), a karty zaczynają się na linii kart profilu.
      className="sticky top-16 z-[9] -mx-4 -mt-4 flex items-end gap-1 px-7 pt-2 lg:-mx-8 lg:-mt-8 lg:px-14"
      style={{
        background: token.colorBgContainer,
        boxShadow: `inset 0 -1px 0 ${token.colorBorderSecondary}`,
      }}
    >
      {/* Przewija się sama lista kart, żeby „+" zawsze był pod ręką. */}
      <div
        ref={listRef}
        role="tablist"
        aria-label="Otwarte klientki"
        className="flex min-w-0 items-end gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        // Kółko myszy przewija karty w poziomie - bez gładzika nie byłoby jak dojść do ukrytych.
        onWheel={(event) => {
          if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            event.currentTarget.scrollLeft += event.deltaY;
          }
        }}
      >
        {traineeIds.map((traineeId) => (
          <TraineeTab
            key={traineeId}
            traineeId={traineeId}
            active={traineeId === activeId}
            fallbackName={tabNames?.get(traineeId)}
            onSelect={onSelect}
            onClose={onClose}
          />
        ))}
      </div>
      <Tooltip title="Otwórz klientkę" placement="bottom">
        <Button
          type="text"
          size="small"
          icon={<PlusOutlined />}
          aria-label="Otwórz klientkę"
          onClick={onAdd}
          className="mb-1.5 shrink-0"
        />
      </Tooltip>
    </div>
  );
};

interface TraineeTabProps {
  traineeId: string;
  active: boolean;
  fallbackName?: TraineeTabName;
  onSelect: (traineeId: string) => void;
  onClose: (traineeId: string) => void;
}

const TraineeTab = ({ traineeId, active, fallbackName, onSelect, onClose }: TraineeTabProps) => {
  const { token } = theme.useToken();
  // Pełny profil tylko z cache (karta widoczna albo już odwiedzona) - bez własnego zapytania.
  // Ma pierwszeństwo, bo odświeża go realtime karty, więc zmiana imienia od razu trafia na pasek.
  const { data: cachedProfile } = useQuery<TraineeProfile>({
    queryKey: traineeKeys.profile(traineeId),
    enabled: false,
  });
  const profile = cachedProfile ?? fallbackName;
  const name = profile ? getTraineeDisplayName(profile) || profile.email || "" : "";
  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={active}
      title={name}
      onClick={() => onSelect(traineeId)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(traineeId);
        }
      }}
      onAuxClick={(event) => {
        if (event.button === 1) {
          event.preventDefault();
          onClose(traineeId);
        }
      }}
      className={`group relative flex h-9 w-[200px] min-w-[112px] shrink cursor-pointer items-center gap-2 rounded-t-lg border border-b-0 border-solid pl-2.5 pr-1.5 text-sm outline-none transition-colors focus-visible:ring-2 ${
        active ? "" : "hover:bg-black/[0.04]"
      }`}
      style={{
        background: active ? token.colorBgLayout : "transparent",
        borderColor: active ? token.colorBorderSecondary : "transparent",
        color: active ? token.colorText : token.colorTextSecondary,
        // Tło aktywnej karty przykrywa dolną linię paska, więc karta przechodzi w treść profilu.
        fontWeight: active ? 500 : 400,
      }}
    >
      <Avatar size={20} icon={<UserOutlined />} src={profile?.user?.avatarUrl} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate capitalize">{name ? name.toLowerCase() : "Wczytywanie..."}</span>
      <button
        type="button"
        aria-label={`Zamknij kartę ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          onClose(traineeId);
        }}
        // Na nieaktywnej karcie krzyżyk zajmuje miejsce dopiero po najechaniu - przy 10 kartach
        // każdy piksel nazwy się liczy.
        className={`h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent p-0 text-[10px] hover:bg-black/[0.08] ${
          active ? "flex" : "hidden group-hover:flex group-focus-visible:flex"
        }`}
        style={{ color: token.colorTextSecondary }}
      >
        <CloseOutlined />
      </button>
    </div>
  );
};
